import { QueryClient } from "@tanstack/react-query";
import { toast } from "react-hot-toast";
import { normalizeError } from "@/core/error/error-types";

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Balanced approach: Fresh enough for collaboration, but reduces unnecessary refetches
      // Individual queries can override with shorter staleTime if needed (e.g., notifications)
      staleTime: 1000 * 30, // 30 seconds (increased from 10s for better performance)
      gcTime: 1000 * 60 * 5, // 5 minutes (formerly cacheTime)
      retry: (failureCount, error) => {
        const appError = normalizeError(error);
        // Auto-retry network/service errors once
        return appError.retryable && failureCount < 1;
      },
      refetchOnWindowFocus: true, // Refetch when user returns to tab
      refetchOnReconnect: true, // Refetch when network reconnects
    },
    mutations: {
      retry: (failureCount, error) => {
        const appError = normalizeError(error);
        // Auto-retry network/service errors once
        return appError.retryable && failureCount < 1;
      },
    },
  },
});

// ============================================================
// Global Error Handling - Toast Notifications
// ============================================================
// This provides centralized error handling for all API errors.
// Components can opt out by setting meta.skipToast = true

// Handle query errors
queryClient.getQueryCache().subscribe((event) => {
  if (event.type === "updated" && event.action?.type === "error") {
    const query = event.query;
    const error = query.state.error;

    if (!error) return;

    // Check if this query should skip global Toast
    // Components can set meta.skipToast = true to handle errors themselves
    if (query.meta?.skipToast) {
      return;
    }

    const appError = normalizeError(error);

    // Skip auth errors (AuthContext handles logout)
    if (appError.code === "UNAUTHENTICATED" || appError.code === "SESSION_EXPIRED") {
      return;
    }

    // Skip NotFound errors (component handles UI - e.g., "Ticker not found" page)
    if (appError.code === "NOT_FOUND") {
      return;
    }

    // Show Toast for other errors
    toast.error(appError.userMessage, {
      id: `query-error-${Array.isArray(query.queryKey) ? query.queryKey[0] : query.queryKey}`,
      duration: 5000,
    });
  }
});

// Handle mutation errors
queryClient.getMutationCache().subscribe((event) => {
  if (event.type === "updated" && event.action?.type === "error") {
    const mutation = event.mutation;
    const error = mutation.state.error;

    if (!error) return;

    // Check if this mutation should skip global Toast
    if (mutation.meta?.skipToast) {
      return;
    }

    const appError = normalizeError(error);

    // Skip auth errors (AuthContext handles logout)
    if (appError.code === "UNAUTHENTICATED" || appError.code === "SESSION_EXPIRED") {
      return;
    }

    // Show Toast for other errors
    toast.error(appError.userMessage, {
      id: `mutation-error-${mutation.mutationId}`,
      duration: 5000,
    });
  }
});
